/**
 * Runtime config for the ADMIN panel.
 *
 * Built once, told at boot where to go, so the panel needs no rebuild when the
 * API moves. Three values are written into config.json; the other two are
 * DERIVED here, on purpose — see below.
 *
 * No mediaDomain on purpose: the admin panel never renders a family's video.
 */

export type RuntimeConfig = {
  /** The staff base URL, INCLUDING the /control prefix. See CONTROL_PREFIX. */
  apiEndpoint: string;
  tenantId: string;
  /** The showling-staff app registration's appId GUID. */
  clientId: string;
  /**
   * What MSAL asks for, e.g. api://<staffAppId>/access.
   *
   * Its own key rather than derived from clientId even though today they name
   * the same registration — entra_bootstrap.sh puts this SPA's redirect URI on
   * the registration that also exposes the API, so the portal requests a token
   * FOR ITSELF and `aud` comes out equal to clientId, which is exactly what
   * auth_setup.sh sets STAFF_AUDIENCE to. The moment the portal gets its own
   * client (which it should, so the console and the API are separable) the two
   * diverge, and a derived value would break SILENTLY: MSAL would still hand
   * back a token and the API would 401 on the `aud` claim. Same shape and same
   * reasoning as web/src/config.ts — the two files are filled in from the same
   * script output and should not disagree about what they need.
   */
  apiScope: string;
  /** Derived. MSAL's authority — deliberately NOT the API's issuer. */
  authority: string;
};

export class ConfigError extends Error {}

const REQUIRED = ['apiEndpoint', 'tenantId', 'clientId', 'apiScope'] as const;

/** What ships in config.local.json.example, and must not survive to Entra. */
const PLACEHOLDER = 'REPLACE_WITH';

/**
 * Every staff route on Azure is under /control, and this is the one place that
 * knows it.
 *
 * The Functions host reserves /admin and /runtime on the app's own hostname
 * when routePrefix is empty, so the staff routes had to move. /control WRAPS
 * rather than replaces: rawPath still contains /admin/..., which is why the 31
 * paths in api.ts are unchanged and only the base URL moved.
 *
 * It is checked rather than appended because the failure mode of a config that
 * forgot it is atrocious: all 29 staff routes answer a bodyless Kestrel 404
 * whose entire message is the string "404", on every screen at once, with
 * nothing naming the cause. A named boot error costs one comparison.
 */
const CONTROL_PREFIX = '/control';

export async function loadConfig(): Promise<RuntimeConfig> {
  const res = await fetch('/config.json', { cache: 'no-store' });

  // A static host that maps a missing object to index.html serves the SPA's own
  // HTML with a perfectly happy 200 — CloudFront did it, and the dev middleware
  // in vite.config.ts mimics it so this path stays exercised. res.ok is a lie
  // here; the JSON parse is the only honest signal. Never branch on status.
  const raw = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new ConfigError(
      'config.json did not parse as JSON — the SPA was served in its place, ' +
        'which means the deployment never wrote it. First bytes: ' +
        JSON.stringify(raw.slice(0, 120)),
    );
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new ConfigError('config.json parsed but is not an object');
  }
  const obj = parsed as Record<string, unknown>;

  const missing = REQUIRED.filter((k) => typeof obj[k] !== 'string' || !obj[k]);
  if (missing.length) {
    throw new ConfigError(`config.json is missing: ${missing.join(', ')}`);
  }

  const apiEndpoint = String(obj.apiEndpoint).replace(/\/+$/, '');
  if (!apiEndpoint.endsWith(CONTROL_PREFIX)) {
    throw new ConfigError(
      `apiEndpoint must end in ${CONTROL_PREFIX} — every staff route lives ` +
        'under it, because the Functions host reserves /admin on its own ' +
        `hostname. Got ${JSON.stringify(apiEndpoint)}; without the prefix ` +
        'every screen in this console 404s with no explanation.',
    );
  }

  const tenantId = String(obj.tenantId);
  const clientId = String(obj.clientId);
  const apiScope = String(obj.apiScope);
  if (clientId.includes(PLACEHOLDER) || apiScope.includes(PLACEHOLDER)) {
    // Caught here so it fails with an instruction, rather than on Microsoft's
    // own page with "Application with identifier 'REPLACE_WITH_…' was not found
    // in the directory" — which reads like an outage and is a blank to fill in.
    throw new ConfigError(
      'clientId/apiScope are still the placeholders from ' +
        'config.local.json.example. The showling-staff app registration does ' +
        'not exist until a human runs azure/entra_bootstrap.sh — the service ' +
        'principal has no Microsoft Graph access — and that script prints the ' +
        'appId to paste into both.',
    );
  }

  return {
    apiEndpoint,
    tenantId,
    clientId,
    apiScope,
    // NO /v2.0, and this is a trap worth stating. MSAL wants
    // https://login.microsoftonline.com/<tenantId>; azure/functions/auth.py
    // wants the ISSUER, which is the same string PLUS /v2.0 (auth_setup.sh
    // sets it). Two strings five characters apart, used by two different
    // processes. Storing the tenant id once and deriving both is what stops a
    // config file from becoming that trap.
    authority: `https://login.microsoftonline.com/${tenantId}`,
  };
}
